"use client";
import type { Meeting } from "@/api/calendar";
import { addDays, format, isSameDay, startOfWeek, endOfWeek } from "date-fns";
import { ar, fr as frLocale } from "date-fns/locale";
import { Plus, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { gen_color } from "@/utils/colors";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatUTCToLocal } from "@/utils/calendar";
import { Holiday } from "@/api/holidays";
import { usePermission } from "@/hooks/use-permission";
import { UserPermission } from "@/constants/permissions";

interface WeekViewProps {
    date: Date;
    meetings: Meeting[];
    holidays: Holiday[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onEventClick: (event: any) => void;
    onAddAppointment: () => void;
    onDayClick: (date: Date) => void;
}

interface ProcessedEvent {
    id: number | string;
    title: string;
    startDate: Date;
    endDate: Date;
    startHour: number;
    endHour: number;
    originalData: Meeting;
    dayOfWeek: number;
}

interface LaidOutEvent extends ProcessedEvent {
    col: number;
    numCols: number;
}

function eventsOverlap(eventA: ProcessedEvent, eventB: ProcessedEvent): boolean {
    if (eventA.dayOfWeek !== eventB.dayOfWeek) return false;
    const buffer = 0.001;
    return eventA.startHour < eventB.endHour - buffer && eventA.endHour > eventB.startHour + buffer;
}

function calculateLayout(events: ProcessedEvent[]): LaidOutEvent[] {
    if (!events || events.length === 0) {
        return [];
    }

    const sortedEvents = [...events].sort((a, b) => {
        if (a.dayOfWeek !== b.dayOfWeek) {
            return a.dayOfWeek - b.dayOfWeek;
        }
        if (a.startHour !== b.startHour) {
            return a.startHour - b.startHour;
        }
        return (b.endHour - b.startHour) - (a.endHour - a.startHour);
    });

    const layout: Array<Omit<LaidOutEvent, keyof ProcessedEvent> & { overlaps: number[] }> = sortedEvents.map(() => ({
        col: -1,
        numCols: 1,
        overlaps: [],
    }));

    for (let i = 0; i < sortedEvents.length; i++) {
        for (let j = i + 1; j < sortedEvents.length; j++) {
            if (eventsOverlap(sortedEvents[i], sortedEvents[j])) {
                layout[i].overlaps.push(j);
                layout[j].overlaps.push(i);
            }
        }
    }

    for (let i = 0; i < sortedEvents.length; i++) {
        const occupiedColumns: boolean[] = [];

        for (const overlapIndex of layout[i].overlaps) {
            if (overlapIndex < i && layout[overlapIndex].col !== -1) {
                occupiedColumns[layout[overlapIndex].col] = true;
            }
        }

        let currentColumn = 0;
        while (occupiedColumns[currentColumn]) {
            currentColumn++;
        }
        layout[i].col = currentColumn;
    }

    for (let i = 0; i < sortedEvents.length; i++) {
        let maxColsInGroup = 1;
        const overlappingIndices = new Set<number>([i]);
        const queue = [...layout[i].overlaps];

        while (queue.length > 0) {
            const currentIndex = queue.shift()!;
            if (!overlappingIndices.has(currentIndex)) {
                overlappingIndices.add(currentIndex);
                layout[currentIndex].overlaps.forEach(nextIndex => {
                    if (!overlappingIndices.has(nextIndex)) {
                        queue.push(nextIndex);
                    }
                });
            }
        }

        overlappingIndices.forEach(idx => {
            maxColsInGroup = Math.max(maxColsInGroup, layout[idx].col + 1);
        })

        overlappingIndices.forEach(idx => {
            layout[idx].numCols = maxColsInGroup;
        });
    }

    return sortedEvents.map((event, index) => ({
        ...event,
        col: layout[index].col,
        numCols: layout[index].numCols,
    }));
}

export function WeekView({
    date,
    meetings,
    holidays,
    onEventClick,
    onAddAppointment,
    onDayClick,
}: WeekViewProps) {
    const { t, i18n } = useTranslation();
    const currentLocale = i18n.language === 'ar' ? ar : frLocale;

    const DEFAULT_START_HOUR = 8;
    const DEFAULT_END_HOUR = 18;

    // Get the start and end of the week
    const weekStart = startOfWeek(date, { weekStartsOn: 1 }); // Monday
    const weekEnd = endOfWeek(date, { weekStartsOn: 1 }); // Sunday

    // Generate array of days in the week
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

    // Process meetings into event objects
    const processedEvents: ProcessedEvent[] = meetings
        .filter((meeting) => {
            if (!meeting.start_time) return false;
            try {
                const meetingDate = new Date(meeting.start_time * 1000);
                return meetingDate >= weekStart && meetingDate <= weekEnd;
            } catch {
                return false;
            }
        })
        .map((meeting) => {
            try {
                const startDate = new Date(meeting.start_time * 1000);
                const endDate = new Date(meeting.end_time ? meeting.end_time * 1000 : meeting.start_time * 1000 + 3600 * 1000);

                const startHour = startDate.getHours() + startDate.getMinutes() / 60;
                let endHour = endDate.getHours() + endDate.getMinutes() / 60;
                if (endHour === 0 && !isSameDay(startDate, endDate)) {
                    endHour = 24;
                }

                const minDuration = 15 / 60;
                endHour = Math.max(endHour, startHour + minDuration);
                endHour = Math.min(endHour, 24);

                // Calculate day of week (0 = Monday, 6 = Sunday)
                const dayOfWeek = Math.floor((startDate.getTime() - weekStart.getTime()) / (24 * 60 * 60 * 1000));

                return {
                    id: meeting.id,
                    title: meeting.desc || `${t("casePrefix")}: ${meeting.case}`,
                    startDate,
                    endDate,
                    startHour: Math.max(0, startHour),
                    endHour,
                    originalData: meeting,
                    dayOfWeek,
                };
            } catch {
                return null;
            }
        })
        .filter((event): event is NonNullable<typeof event> => event !== null && event.startHour < event.endHour);

    // Determine visible hour range
    let visibleStartHour = DEFAULT_START_HOUR;
    let visibleEndHour = DEFAULT_END_HOUR;

    if (processedEvents.length > 0) {
        const eventStartHours = processedEvents.map(e => Math.floor(e.startHour));
        const eventEndHours = processedEvents.map(e => Math.ceil(e.endHour));

        const minEventHour = Math.min(...eventStartHours);
        const maxEventHour = Math.max(...eventEndHours);

        if (minEventHour < DEFAULT_START_HOUR) {
            visibleStartHour = minEventHour;
        }

        if (maxEventHour > DEFAULT_END_HOUR) {
            visibleEndHour = maxEventHour;
        }
    }

    const visibleHours = Array.from(
        { length: visibleEndHour - visibleStartHour },
        (_, i) => i + visibleStartHour
    );

    const getEventVerticalPosition = (startHour: number, endHour: number) => {
        const totalVisibleHours = visibleEndHour - visibleStartHour;

        const clampedStartHour = Math.max(startHour, visibleStartHour);
        const clampedEndHour = Math.min(endHour, visibleEndHour);

        const relativeStart = clampedStartHour - visibleStartHour;
        const duration = clampedEndHour - clampedStartHour;

        const top = (relativeStart / totalVisibleHours) * 100;
        const height = (duration / totalVisibleHours) * 100;

        return { top: `${top}%`, height: `${Math.max(height, 1.0)}%` };
    };

    const laidOutEvents = calculateLayout(processedEvents);

    const renderEvent = (laidOutEvent: LaidOutEvent) => {
        const { event, col, numCols } = {
            event: laidOutEvent,
            col: laidOutEvent.col,
            numCols: laidOutEvent.numCols,
        };

        if (event.startHour >= event.endHour) {
            return null;
        }

        const { top, height } = getEventVerticalPosition(
            event.startHour,
            event.endHour
        );

        const columnWidthPercent = 100 / numCols;
        const leftPercent = col * columnWidthPercent;
        const horizontalGap = 2;

        const widthStyle = `calc(${columnWidthPercent}% - ${horizontalGap}px)`;
        const leftStyle = `calc(${leftPercent}% + ${horizontalGap / 2}px)`;

        const classes = gen_color(event.originalData.assigned_to?.name || 'default');

        return (
            <Tooltip key={event.id} delayDuration={100}>
                <TooltipTrigger asChild>
                    <div
                        className={`${classes.textColorClass} bg-opacity-80 absolute p-1 overflow-hidden rounded cursor-pointer shadow border dark:border-gray-600 ${event.originalData.is_postponed ? "opacity-60" : ""
                            }`}
                        style={{
                            top,
                            height,
                            left: leftStyle,
                            width: widthStyle,
                            backgroundColor: classes.backgroundColor,
                            borderColor: classes.borderColor,
                            zIndex: 10 + col,
                        }}
                        onClick={() => onEventClick({
                            ...event,
                            color: { backgroundColor: classes.backgroundColor, borderColor: classes.borderColor },
                            text_classes: classes.textColorClass,
                        })}
                    >
                        <div className={`text-[10px] font-semibold truncate ${event.originalData.is_postponed ? "line-through" : ""}`}>
                            {event.originalData.postponed_from && (
                                <span className="ml-1 inline-block">
                                    <Clock className="w-2 h-2 inline" />
                                </span>
                            )}
                            {event.title}
                        </div>
                        {(parseFloat(height) > 4) && (
                            <div className={`text-[9px] opacity-90 ${event.originalData.is_postponed ? "line-through" : ""}`}>
                                {format(event.startDate, 'HH:mm')}
                            </div>
                        )}
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                    <div>
                        <div className="font-semibold">{event.title}</div>
                        <div>
                            {formatUTCToLocal(
                                new Date(event.originalData.start_time * 1000),
                                "yyyy-MM-dd HH:mm"
                            )}
                            {event.originalData.end_time && (
                                <> - {formatUTCToLocal(
                                    new Date(event.originalData.end_time * 1000),
                                    "HH:mm"
                                )}</>
                            )}
                        </div>
                        <div>
                            {t('assigned_to')}: {event.originalData.assigned_to.name}
                        </div>
                        {event.originalData.tribunal_proc_type && (
                            <div>
                                {event.originalData.tribunal_proc_type}
                            </div>
                        )}
                        {event.originalData.is_postponed && (
                            <div className="text-amber-500 dark:text-amber-400">
                                {t("meetingPostponed")}
                            </div>
                        )}
                        {event.originalData.postponed_from && (
                            <div>
                                {t("postponedFromMsg", {
                                    date: formatUTCToLocal(
                                        new Date(event.originalData.postponed_from.start_time * 1000),
                                        "yyyy-MM-dd HH:mm"
                                    )
                                })}
                            </div>
                        )}
                    </div>
                </TooltipContent>
            </Tooltip>
        );
    };

    const today = new Date();
    const { can } = usePermission();

    return (
        <TooltipProvider>
            {can(UserPermission.ADD_RENDEZVOUS) && (
                <Button
                    onClick={onAddAppointment}
                    className="fixed z-50 bg-blue-600 shadow-lg bottom-4 left-4 md:bottom-6 md:left-6 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 p-2 md:p-3 rounded-full"
                    aria-label={t("addNewAppointment")}
                >
                    <span className="hidden md:inline mr-1">{t("addNewAppointment")}</span>
                    <Plus className="w-4 h-4 md:w-5 md:h-5" />
                </Button>
            )}

            <div className="flex flex-col h-full bg-white border border-gray-200 rounded-lg shadow-sm dark:bg-gray-900 dark:border-gray-700 overflow-hidden mt-3">
                {/* Week header with days - horizontally scrollable on mobile */}
                <div className="sticky top-0 z-30 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
                    <div className="flex min-w-[600px] md:min-w-0">
                        {/* Time column header */}
                        <div className="w-12 md:w-16 shrink-0 border-r border-gray-200 dark:border-gray-700"></div>

                        {/* Day headers */}
                        {weekDays.map((day, index) => {
                            const isToday = isSameDay(day, today);
                            const dayHolidays = holidays.filter(h => isSameDay(new Date(h.date), day));
                            const hasHoliday = dayHolidays.length > 0;
                            return (
                                <div
                                    key={index}
                                    className={`flex-1 min-w-[70px] md:min-w-0 text-center py-1.5 md:py-2 border-r border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${hasHoliday ? 'bg-red-50 dark:bg-red-950' : ''
                                        }`}
                                    onClick={() => onDayClick(day)}
                                >
                                    <div className={`text-[10px] md:text-xs font-medium ${isToday ? 'text-blue-600 dark:text-blue-400' : hasHoliday ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}>
                                        {format(day, 'EEE', { locale: currentLocale })}
                                    </div>
                                    <div className={`text-sm md:text-lg font-bold ${isToday ? 'text-blue-600 dark:text-blue-400' : hasHoliday ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>
                                        {format(day, 'd')}
                                    </div>
                                    {hasHoliday && (
                                        <div className="text-[9px] text-red-600 dark:text-red-400 truncate px-1">
                                            {i18n.language === 'ar' ? dayHolidays[0].name_ar : (i18n.language === 'fr' && dayHolidays[0].name_fr ? dayHolidays[0].name_fr : (dayHolidays[0].name || dayHolidays[0].name_en))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Scrollable week content - horizontally scrollable on mobile */}
                <div className="flex-1 overflow-auto">
                    <div className="relative min-w-[600px] md:min-w-0" style={{ height: `${(visibleEndHour - visibleStartHour) * 60}px` }}>
                        {/* Hour lines */}
                        {visibleHours.map((hour, i) => (
                            <div
                                key={`hour-${i}`}
                                className="absolute flex w-full border-t border-gray-200 dark:border-gray-700"
                                style={{ top: `${(i / (visibleEndHour - visibleStartHour)) * 100}%` }}
                            >
                                {/* Hour Label */}
                                <div className={`w-12 md:w-16 pr-1 md:pr-2 text-[10px] md:text-xs text-right text-gray-500 dark:text-gray-400 shrink-0 ${i === 0 ? 'pt-1' : '-mt-3'}`}>
                                    {`${hour.toString().padStart(2, '0')}:00`}
                                </div>
                                {/* Day columns */}
                                {weekDays.map((_, dayIndex) => (
                                    <div
                                        key={`day-${dayIndex}`}
                                        className="flex-1 min-w-[70px] md:min-w-0 border-r border-gray-200 dark:border-gray-700"
                                    />
                                ))}
                            </div>
                        ))}

                        {/* Event containers wrapper - positioned after time column */}
                        <div className="absolute top-0 bottom-0 left-12 md:left-16 right-0">
                            {/* Event containers for each day */}
                            {weekDays.map((_, dayIndex) => {
                                const dayEvents = laidOutEvents.filter(e => e.dayOfWeek === dayIndex);
                                const dayWidth = 100 / 7;
                                return (
                                    <div
                                        key={`events-${dayIndex}`}
                                        className="absolute top-0 bottom-0"
                                        style={{
                                            left: `${dayIndex * dayWidth}%`,
                                            width: `${dayWidth}%`,
                                        }}
                                    >
                                        {dayEvents.map(renderEvent)}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Message when no events */}
                        {laidOutEvents.length === 0 && (
                            <div className="absolute inset-0 flex items-center justify-center pt-10 text-gray-500 pointer-events-none">
                                {t("noAppointments")}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </TooltipProvider>
    );
}
